import type { AnchorShape } from '@discovr/types';

import { createCanvas2d } from '../../../../../lib/browser/canvas';
import { encodeCanvas, IMAGE_QUALITY, releaseCanvas, type CompressedImage } from '../../../../../lib/media/compressImage';

import type { DraftShape } from '../lib/annotationReducer';

const MIN_STROKE_PX = 2;
/**
 * An 8px stroke on a 704px-wide frame. The short side sets the weight so a
 * landscape and a portrait cut carry the same visual line.
 */
const STROKE_DIVISOR = 88;
/** A 34px chevron behind an 8px stroke, opened ±0.42 rad around the shaft. */
const ARROW_HEAD_FACTOR = 4.25;
const ARROW_HEAD_ANGLE = 0.42;
const WHITE = '#FFFFFF';
const SHADOW_COLOUR = 'rgba(0,0,0,0.8)';
const SHADOW_BLUR_FACTOR = 1.5;

export interface PixelBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Pure: a box/arrow's fractions → pixels on a width×height canvas. */
export function shapeToPixels(shape: AnchorShape, width: number, height: number): PixelBox {
  const x1 = (shape.x1 ?? 0) * width;
  const y1 = (shape.y1 ?? 0) * height;
  const x2 = (shape.x2 ?? 0) * width;
  const y2 = (shape.y2 ?? 0) * height;
  return { x: Math.min(x1, x2), y: Math.min(y1, y2), w: Math.abs(x2 - x1), h: Math.abs(y2 - y1) };
}

/** Pure: a pen point in fractions → pixels. */
export function pointToPixels(pt: [number, number], width: number, height: number): [number, number] {
  return [pt[0] * width, pt[1] * height];
}

export function strokeWidthFor(width: number, height: number): number {
  return Math.max(MIN_STROKE_PX, Math.min(width, height) / STROKE_DIVISOR);
}

export type Point = [number, number];

/**
 * Pure: the two open ends of an arrow head, in the same units as the shaft.
 * Shared by the live overlay and the bake so the mark never changes shape
 * between what the reviewer drew and what the note stores.
 */
export function arrowHeadPoints(x1: number, y1: number, x2: number, y2: number, stroke: number): [Point, Point] {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const length = stroke * ARROW_HEAD_FACTOR;
  return [
    [x2 - length * Math.cos(angle - ARROW_HEAD_ANGLE), y2 - length * Math.sin(angle - ARROW_HEAD_ANGLE)],
    [x2 - length * Math.cos(angle + ARROW_HEAD_ANGLE), y2 - length * Math.sin(angle + ARROW_HEAD_ANGLE)],
  ];
}

function drawArrow(ctx: CanvasRenderingContext2D, shape: AnchorShape, width: number, height: number, stroke: number) {
  const x1 = (shape.x1 ?? 0) * width;
  const y1 = (shape.y1 ?? 0) * height;
  const x2 = (shape.x2 ?? 0) * width;
  const y2 = (shape.y2 ?? 0) * height;
  const [left, right] = arrowHeadPoints(x1, y1, x2, y2, stroke);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  // One open path through the tip, so the join is rounded.
  ctx.beginPath();
  ctx.moveTo(left[0], left[1]);
  ctx.lineTo(x2, y2);
  ctx.lineTo(right[0], right[1]);
  ctx.stroke();
}

function drawPen(ctx: CanvasRenderingContext2D, pts: [number, number][], width: number, height: number) {
  if (pts.length < 2) return;
  ctx.beginPath();
  const [sx, sy] = pointToPixels(pts[0] as [number, number], width, height);
  ctx.moveTo(sx, sy);
  for (let i = 1; i < pts.length; i += 1) {
    const [x, y] = pointToPixels(pts[i] as [number, number], width, height);
    ctx.lineTo(x, y);
  }
  ctx.stroke();
}

function drawShape(ctx: CanvasRenderingContext2D, shape: DraftShape, width: number, height: number, stroke: number) {
  ctx.strokeStyle = shape.c;
  ctx.lineWidth = stroke;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // White ink is invisible on a bright frame — the same shadow the live overlay draws.
  const isWhite = shape.c.toUpperCase() === WHITE;
  ctx.shadowColor = isWhite ? SHADOW_COLOUR : 'transparent';
  ctx.shadowBlur = isWhite ? stroke * SHADOW_BLUR_FACTOR : 0;

  switch (shape.k) {
    case 'box': {
      const b = shapeToPixels(shape, width, height);
      ctx.strokeRect(b.x, b.y, b.w, b.h);
      return;
    }
    case 'arrow':
      drawArrow(ctx, shape, width, height, stroke);
      return;
    case 'pen':
      // The raw path (bake only) keeps every point; storage is decimated.
      drawPen(ctx, shape.rawPts ?? shape.pts ?? [], width, height);
      return;
  }
}

/** A baked frame is compressed the same way as any other image attachment. */
export type BakedFrame = CompressedImage;

/** Lower qualities a frame over its byte limit is re-encoded at, in turn, before it's refused. */
export const FRAME_FALLBACK_QUALITIES: readonly number[] = [0.6, 0.45];

/** Every quality a frame is tried at, best first: the shared compression quality, then the fallbacks. */
const FRAME_QUALITY_LADDER: readonly number[] = [IMAGE_QUALITY, ...FRAME_FALLBACK_QUALITIES];

function drawFrame(bitmap: ImageBitmap, shapes: DraftShape[]): HTMLCanvasElement {
  const width = bitmap.width;
  const height = bitmap.height;
  const { canvas, ctx } = createCanvas2d(width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  const stroke = strokeWidthFor(width, height);
  for (const shape of shapes) {
    drawShape(ctx, shape, width, height, stroke);
  }
  return canvas;
}

function fitsWithin(frame: BakedFrame, maxBytes: number): boolean {
  return frame.blob.size <= maxBytes;
}

/**
 * Draws the bitmap and every shape, and exports it as WebP (JPEG where WebP
 * can't be encoded) at the shared quality — or, while it's over `maxBytes`,
 * at each lower fallback quality in turn. Resolves null when even the lowest
 * is over: the frame can't be attached.
 */
export async function bakeFrame(bitmap: ImageBitmap, shapes: DraftShape[], maxBytes: number): Promise<BakedFrame | null> {
  const canvas = drawFrame(bitmap, shapes);
  try {
    for (const quality of FRAME_QUALITY_LADDER) {
      const baked = await encodeCanvas(canvas, quality);
      if (fitsWithin(baked, maxBytes)) return baked;
    }
    return null;
  } finally {
    releaseCanvas(canvas);
  }
}
